import polygonClipping from "polygon-clipping";

// Coordinates in the app are [{lat,lng}]. polygon-clipping wants rings of
// [lng,lat] pairs, closed. These helpers convert both ways and keep only clean
// numeric points.
function toRing(coords) {
  const pts = (coords || [])
    .filter((c) => Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng)))
    .map((c) => [Number(c.lng), Number(c.lat)]);
  if (pts.length < 3) return null;
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) pts.push([first[0], first[1]]); // close
  return [pts]; // a polygon = [outerRing]
}

// A clipped result may be a MultiPolygon; take the largest outer ring (by area)
// so an area stays a single simple shape.
function ringArea(ring) {
  let a = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    a += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
  }
  return Math.abs(a / 2);
}
function largestOuterRing(multi) {
  let best = null;
  let bestArea = -1;
  for (const poly of multi) {
    const outer = poly[0];
    const ar = ringArea(outer);
    if (ar > bestArea) { bestArea = ar; best = outer; }
  }
  return best;
}
function ringToCoords(ring) {
  const pts = ring.slice();
  // drop the closing duplicate point
  if (pts.length > 1) {
    const f = pts[0], l = pts[pts.length - 1];
    if (f[0] === l[0] && f[1] === l[1]) pts.pop();
  }
  return pts.map(([lng, lat]) => ({ lat: Math.round(lat * 1e6) / 1e6, lng: Math.round(lng * 1e6) / 1e6 }));
}

/**
 * Clip one area's coordinates so it does not overlap any of the `others`.
 * Returns adjusted coordinates, or the original if nothing overlapped / clipping
 * couldn't produce a clean single shape. Never throws.
 */
export function clipAgainstOthers(coords, othersCoordsList) {
  const subject = toRing(coords);
  if (!subject) return { coords, changed: false };

  const clips = (othersCoordsList || []).map(toRing).filter(Boolean);
  if (!clips.length) return { coords, changed: false };

  try {
    // Only clip if there is an actual overlap (avoids needless reshaping).
    const overlap = polygonClipping.intersection(subject, ...clips);
    if (!overlap.length) return { coords, changed: false };

    const diff = polygonClipping.difference(subject, ...clips);
    if (!diff.length) return { coords, changed: false }; // fully covered — leave as-is

    const outer = largestOuterRing(diff);
    if (!outer || outer.length < 4) return { coords, changed: false };

    return { coords: ringToCoords(outer), changed: true };
  } catch {
    return { coords, changed: false };
  }
}

/**
 * Normalise a whole list of zones so none overlap: each zone is clipped against
 * the ones before it (a simple, stable ordering). Returns { zones, changed }.
 */
export function normalizeZones(zonesList) {
  const out = [];
  let changed = false;
  for (const z of zonesList) {
    const res = clipAgainstOthers(z.coordinates, out.map((o) => o.coordinates));
    if (res.changed) changed = true;
    out.push({ ...z, coordinates: res.coords });
  }
  return { zones: out, changed };
}
